import fp from 'lodash/fp';
import he from 'he';
import { pageSizes, cpdCategoryType } from '../constants';
import CaddieLogo from '../images/logo.png';

const chartOffset = { x: 110, y: 0 };
const tableOffset = { x: -110, y: 60 };

const fetchImage = async (src) => {
  const response = await fetch(src);
  const image = await response.arrayBuffer();

  return Buffer.from(image, 'base64');
};

const certificateBorder =  (pageWidth, pageHeight, outerBorderWidth, innerBorderWidth) => [
  // Draw certificate outer border
  ['save'],
  ['rect', [0, 0, pageWidth, outerBorderWidth]],
  ['fill', ['#1fd1a1']],
  ['fillColour', ['#fff']],
  ['save'],
  ['rect', [0, 0, outerBorderWidth, pageHeight]],
  ['fill', ['#1fd1a1']],
  ['fillColour', ['#fff']],
  ['save'],
  ['rect', [pageWidth - outerBorderWidth, 0, outerBorderWidth, pageHeight]],
  ['fill', ['#1fd1a1']],
  ['fillColour', ['#fff']],
  ['save'],
  ['rect', [0, pageHeight - outerBorderWidth, pageWidth, outerBorderWidth]],
  ['fill', ['#1fd1a1']],
  ['fillColour', ['#fff']],
  ['restore'],

  // Draw certificate inner border
  ['save'],
  ['rect', [outerBorderWidth, outerBorderWidth, pageWidth - 2 * outerBorderWidth, innerBorderWidth]],
  ['fill', ['#00ff65']],
  ['fillColour', ['#fff']],
  ['save'],
  ['rect', [outerBorderWidth, outerBorderWidth, innerBorderWidth, pageHeight - 2 * outerBorderWidth]],
  ['fill', ['#00ff65']],
  ['fillColour', ['#fff']],
  ['save'],
  ['rect', [pageWidth - outerBorderWidth - innerBorderWidth, outerBorderWidth, innerBorderWidth, pageHeight - 2 * outerBorderWidth]],
  ['fill', ['#00ff65']],
  ['fillColour', ['#fff']],
  ['save'],
  ['rect', [outerBorderWidth, pageHeight - outerBorderWidth - innerBorderWidth, pageWidth - 2 * outerBorderWidth, innerBorderWidth]],
  ['fill', ['#00ff65']],
  ['fillColour', ['#fff']],

  ['restore'],
];

const cpdActivityCertificate = async ({
  userName,
  contentTitle,
  publisherName,
  publisherLogo,
  cpdPointAllocation,
  totalPoints,
  dateCompleted,
  contentId,
}) => {
  const pageWidth = pageSizes.A4.x;
  const pageHeight = pageSizes.A4.y;
  const outerBorderWidth = 8;
  const innerBorderWidth = 2;
  const newLineGap = 40;
  const certificateTextStartHeight = pageHeight / 8;

  const logoUrl = publisherLogo !== "" ? he.decode(publisherLogo) : CaddieLogo;
  
  const logo = await fetchImage(logoUrl);

  const activityPublisherText = `Present by: ${publisherName}\nCompletion date: ${dateCompleted}`;
  const activityStatText = `Total hours: ${totalPoints}hrs\nIssue Date: ${dateCompleted}\nCaddie accreditation id: ${contentId}`;
  const activityCorePointsAllocationText = cpdPointAllocation
    .filter((point) => point.categoryType === cpdCategoryType.core)
    .map((point) => `${point.name} - ${point.points}hrs`)
    .join('\n');
  const activitySubPointsAllocationText = cpdPointAllocation
    .filter((point) => point.categoryType === cpdCategoryType.sub)
    .map((point) => `${point.name} - ${point.points}hrs`)
    .join('\n');
  const certificateStatementText = 'This activity has been accredited as continuing professional development by Caddie in accordance with the ThinkCaddie CPD policy which can be found';

  return [
    ...certificateBorder(pageWidth, pageHeight, outerBorderWidth, innerBorderWidth),

    ['translate', fp.values(chartOffset)],
    ['translate', fp.values(tableOffset)],

    ['image', [logo, pageWidth - 150, 0, { fit: [100, 50], align: 'center', valign: 'center' }]],

    ['fillColour', ['grey']],
    ['fontSize', [30]],
    ['font', ['Helvetica-Bold']],
    ['text', ['Certificate of Completion', 0, certificateTextStartHeight, { align: 'center', width: pageWidth }]],
    ['fontSize', [15]],
    ['font', ['Helvetica']],
    ['text', ['This is to certify that', 0, certificateTextStartHeight + newLineGap, { align: 'center', width: pageWidth }]],
    ['fontSize', [20]],
    ['text', [userName, 0, certificateTextStartHeight + 2 * newLineGap, { align: 'center', width: pageWidth }]],
    ['fontSize', [15]],
    ['text', ['Has completed the following activity', 0, certificateTextStartHeight + 3 * newLineGap, { align: 'center', width: pageWidth }]],
    ['text', [contentTitle , 0, certificateTextStartHeight + 4 * newLineGap, { align: 'center', width: pageWidth }]],
    ['text', [activityPublisherText, 0, certificateTextStartHeight + 5 * newLineGap, { align: 'center', width: pageWidth }]],

    ['fontSize', [20]],
    ['font', ['Helvetica-Bold']],
    ['text', ['Legislative and ASIC competencies covered:', 0, certificateTextStartHeight + 6.5 * newLineGap, { align: 'center', width: pageWidth }]],
    ['fontSize', [15]],
    ['text', ['Legislated CPD Area', 50, certificateTextStartHeight + 7.5 * newLineGap, { align: 'center', width: pageWidth / 2 - 50 }]],
    ['text', ['ASIC Knowledge Requirements', pageWidth / 2, certificateTextStartHeight + 7.5 * newLineGap, { align: 'center', width: pageWidth / 2 - 50 }]],
    ['font', ['Helvetica']],
    ['text', ['\n' + activityCorePointsAllocationText, 50, certificateTextStartHeight + 7.5 * newLineGap, { align: 'center', width: pageWidth / 2 - 50 }]],
    ['text', ['\n' + activitySubPointsAllocationText, pageWidth / 2, certificateTextStartHeight + 7.5 * newLineGap, { align: 'center', width: pageWidth / 2 - 50 }]],

    ['text', [activityStatText, 5 * outerBorderWidth, pageHeight - 5 * newLineGap, { align: 'center', width: pageWidth - 10 * outerBorderWidth }]],

    ['fontSize', [10]],
    ['font', ['Helvetica']],
    ['text', [certificateStatementText, 5 * outerBorderWidth, pageHeight - 3 * newLineGap, { align: 'center', width: pageWidth - 10 * outerBorderWidth }]],
    ['font', ['Helvetica-Bold']],
    ['text', ['here', pageWidth / 2 + 101, pageHeight - 3 * newLineGap + 11.5, { 
      continued: true,
      underline: true,
      link: 'https://caddie-public-document.s3.ap-southeast-2.amazonaws.com/ThinkCaddie+CPD+Policy.pdf' 
    }]],

  ];
};

export default cpdActivityCertificate;



// WEBPACK FOOTER //
// ./src/reports/cpd-activity-certificate.js